<template>
  <!--
    紧贴输入框下沿向下展开。定位锚点由父组件给：一个贴在输入框左下角的 0×0 空元素
    （见 comment/index.vue 里的 .mention-anchor）。
    原来锚在工具栏的 @ 按钮上，面板就跑到工具栏下方去了 —— 离用户正在打的字很远；
    改成向上展开又会整块压在输入框上，挡住刚打的 "@张"。锚到输入框下沿两个问题都没有。
    下方空间不够时 popper.js 会自己翻到上方（flip 默认开启）。

    仍然用 el-popover 而不是自己写一个绝对定位的下拉：popper 会挂到 body 下，
    不会被外层 .article-dialog-content（overflow: auto）裁掉。

    trigger 保持 click：锚点是 0×0 的空元素，点不到，所以面板只会由父组件的
    open() 打开；而 Element 在 click 模式下顺带绑的 document 点击监听正好帮我们
    实现了"点面板外面关闭"，不用自己写一套。

    visible-arrow=false：气泡箭头是"提示"的语汇，这是一个选人浮层，不需要。
  -->
  <el-popover
    ref="popover"
    placement="bottom-start"
    trigger="click"
    :width="320"
    :visible-arrow="false"
    v-model="popoverVisible"
    popper-class="mention-popover"
    @show="onShow"
  >
    <div class="mention-container">
      <!--
        面板里没有搜索框、没有标题、也没有"已选 N/10"计数 —— 只有一列候选人。

        搜索词来自用户在正文里 "@" 后面接着打的字（父组件用 detectMentionQuery 取出来
        再通过 open(keyword) 传进来），这是 @ 功能的通行设计：牛客、微博、Slack
        都是"打 @ 然后接着打名字"，面板只负责列出结果。
        面板里再放一个搜索框等于让用户在两个地方输入同一件事，而且"在哪打字"这件事
        本身就变得含糊 —— 打在正文里会改评论内容，打在面板里不会。
      -->

      <!-- 首屏 10 个，滚到底加载下一页 -->
      <div class="mention-list" ref="listRef" @scroll="onListScroll">
        <div v-if="loading && !list.length" class="mention-tip">加载中...</div>
        <div v-else-if="!loading && !list.length" class="mention-tip">
          {{ keyword ? '没有找到这个人' : '还没有可以@的人' }}
        </div>

        <div
          v-for="item in list"
          :key="item.userId"
          class="mention-item hand-style"
          :class="{ 'is-selected': isSelected(item) }"
          @click="onSelect(item)"
        >
          <!-- 只显示头像 + 昵称，不显示平台账号（与 App 端一致） -->
          <smart-img mode="avatar" :src="item.avatar" :alt="item.nickname" class="mention-avatar" />
          <span class="mention-nickname">{{ item.nickname }}</span>
          <i v-if="isSelected(item)" class="el-icon-check mention-check"></i>
        </div>

        <!-- 只在翻页时给一行"加载中"。不再显示"没有更多了" ——
             候选人列表本来就是给人扫一眼点一下的，翻到底提示一句只是噪音 -->
        <div v-if="list.length && loading" class="mention-tip is-more">加载中...</div>
      </div>
    </div>

    <template slot="reference">
      <slot></slot>
    </template>
  </el-popover>
</template>

<script>
/**
 * 评论区 @ 选人面板
 *
 * 候选人来自 /v1/comment/atCandidates：不输入关键词时给「我经常 @ 的人 + 我关注的人」，
 * 输入关键词后全站搜人（不限于好友），但把我关注的人排在前面 ——
 * 想 @ 的人绝大多数在关注列表里，偶尔要 @ 一个陌生人时也不该找不到。
 *
 * 抽成组件是因为主评论框（components/comment/index.vue）和回复框
 * （components/comment/Reply.vue）都要用，两边的输入逻辑本来就已经各写了一份，
 * 选人这块不该再抄第三遍。
 */
import { getAtCandidates } from '@/api/userCenter'
import { MAX_AT_USER_COUNT } from '@/utils/mention'

// 搜索防抖。逐字请求在弱网下会让后发的请求先回，把列表刷成乱序；
// 接口本身也带限流（60 秒 30 次），不防抖连打十几个字就可能被拦
const SEARCH_DEBOUNCE = 300

// 距列表底部多少像素触发翻页
const SCROLL_THRESHOLD = 40

export default {
  name: 'MentionPicker',

  props: {
    // 已经 @ 过的用户 id，用来在列表里打勾并挡住重复选择
    selectedIds: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      popoverVisible: false,
      keyword: '',
      list: [],
      loading: false,
      // 分页：首屏 10 个（页大小由后端定），滚到底加载下一页
      pageNo: 1,
      hasMore: true,
      searchTimer: null,
      // 请求序号，用来丢弃过期响应，见 fetchList 里的说明
      requestSeq: 0
    }
  },

  beforeDestroy () {
    this.clearSearchTimer()
  },

  methods: {
    // 每次打开都重新拉一遍：用户可能在别处新关注了人，
    // 缓存住上次的列表会让他找不到刚关注的那个。
    //
    // 面板只有一条打开路径 —— 父组件的 open(keyword)，它会先把 keyword 设好再置
    // popoverVisible，所以这里直接用当前 keyword 拉取即可。
    // （锚点是 0×0 的空元素，用户点不到，不存在"绕过 open() 被点开"这条路径。）
    onShow () {
      this.reset()
      this.fetchList()
    },

    /**
     * 供父组件调用：正文里出现「正在输入的 @」时把面板顶出来，并跟着关键词刷新。
     *
     * @param {String} keyword 用户在正文里 "@" 后面已经输入的内容。
     *   面板自己没有搜索框，搜什么完全由这个参数决定
     */
    open (keyword) {
      const next = typeof keyword === 'string' ? keyword : ''
      if (this.popoverVisible) {
        // 已经开着：只跟着关键词走，不重开面板（重开会让 @show 再跑一次，列表闪一下）
        if (this.keyword !== next) {
          this.keyword = next
          this.scheduleRefresh()
        }
        return
      }
      this.keyword = next
      this.popoverVisible = true
    },

    close () {
      this.popoverVisible = false
    },

    /**
     * 重新按锚点算一次位置。
     *
     * 父组件会把锚点挪到正文里那个 "@" 的像素位置上（见 updateMentionAnchor），
     * 而 popper.js 只在创建、页面滚动、resize 时才重新读锚点的位置 ——
     * 锚点是被内联 style 挪的，它感知不到，必须显式让它重算。
     */
    reposition () {
      if (!this.popoverVisible) return
      const popover = this.$refs.popover
      if (popover && typeof popover.updatePopper === 'function') {
        popover.updatePopper()
      }
    },

    reset () {
      this.list = []
      this.pageNo = 1
      this.hasMore = true
      // 必须把在途标记一起放开，否则改关键词时新请求会被上一次的锁挡掉，
      // 列表停在旧结果上。丢弃旧响应靠下面的 requestSeq
      this.loading = false
      this.requestSeq += 1
    },

    /**
     * 拉取候选人。
     *
     * @param {Boolean} append true 表示翻页（追加到列表尾部），否则是首屏（整体替换）
     */
    fetchList (append) {
      if (this.loading) return
      this.loading = true

      // 请求序号：reset() 会放开在途锁，于是同时可能有多个请求在飞。
      // 弱网下 "张" 的慢响应会晚于 "张三" 落地，直接整体覆盖列表，
      // 用户看到的是和搜索框对不上的结果。只认最后一次发出的请求。
      const seq = this.requestSeq

      // keyword 为空给「常用@的人 + 关注的人」，非空全站搜人。
      // 后端已经按「可被 @」的口径过滤过（实名主身份、未注销未封禁），
      // 与落库时完全一致，所以这里不用再筛一遍 —— 面板里能选到的人就一定能被 @ 上。
      //
      // 网页端这个面板是竖向列表、高度有限，滚到底继续加载下一页
      const pageNo = append ? this.pageNo : 1
      getAtCandidates({ keyword: this.keyword.trim(), pageNo }).then(res => {
        if (seq !== this.requestSeq) return
        if (res.code !== 200 || !res.data) {
          if (!append) this.list = []
          this.hasMore = false
          return
        }
        const records = res.data.records || []
        if (append) {
          // 翻页去重：两次请求之间「常用@的人」的排序可能已经变了
          const existing = new Set(this.list.map(item => String(item.userId)))
          this.list = [...this.list, ...records.filter(item => !existing.has(String(item.userId)))]
        } else {
          this.list = records
          this.pageNo = 1
        }
        this.hasMore = res.data.hasMore === true
      }).catch(err => {
        if (seq !== this.requestSeq) return
        if (!append) this.list = []
        // 翻页失败要把页码退回去，否则下次滚到底会跳过这一页
        if (append && this.pageNo > 1) this.pageNo--
        // 未登录/被封禁走的是全局哨兵错误，这里静默即可，别再弹一个红条
        if (err?.message !== '__SILENT_AUTH__' && err?.message !== '__ACCOUNT_BANNED__') {
          console.error('获取@候选人失败:', err)
        }
      }).finally(() => {
        if (seq !== this.requestSeq) return
        this.loading = false
      })
    },

    // 滚到底：加载下一页
    onListScroll () {
      const el = this.$refs.listRef
      if (!el || this.loading || !this.hasMore || !this.list.length) return
      if (el.scrollHeight - el.scrollTop - el.clientHeight > SCROLL_THRESHOLD) return
      this.pageNo += 1
      this.fetchList(true)
    },

    // 关键词变了之后延迟重拉。用户是在正文里逐字打名字的，
    // 每个字都发一次请求既会被接口限流拦下、也会因为弱网下响应乱序把列表刷花
    scheduleRefresh () {
      this.clearSearchTimer()
      this.searchTimer = setTimeout(() => {
        this.searchTimer = null
        this.reset()
        this.fetchList()
      }, SEARCH_DEBOUNCE)
    },

    isSelected (item) {
      if (!item) return false
      const id = String(item.userId || '')
      return !!id && this.selectedIds.some(selected => String(selected) === id)
    },

    // 不校验匿名/注销/封禁：后端 selectAtCandidates 的过滤口径
    // （nick_type = 0 且 status not in (0,2)）与落库时的 resolveAtUsers 完全一致，
    // 「能选到的人」就是「一定能被 @ 上的人」。CommentAtCandidateVO 也不返回这几个字段，
    // 之前那几行判断是永远不成立的死代码。
    onSelect (item) {
      if (!item) return
      if (this.isSelected(item)) {
        this.$toast.info('已经@过这个人了')
        return
      }
      if (this.selectedIds.length >= MAX_AT_USER_COUNT) {
        this.$toast.info(`最多只能@${MAX_AT_USER_COUNT}个人`)
        return
      }
      // 字段名只认 userId：候选人统一来自 /v1/comment/atCandidates，
      // 它返回的就是 userId，留 `|| item.id` 的兜底只会掩盖字段名写错这类问题
      this.$emit('select', {
        userId: String(item.userId || ''),
        nickname: String(item.nickname || '')
      })
      this.popoverVisible = false
    },

    clearSearchTimer () {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
        this.searchTimer = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.mention-container {
  width: 100%;
}

/* 限高 + overflow 是 onListScroll 能触发的前提。
   264px ≈ 5 行（每行 52px），再高会把评论列表遮掉一大片 */
.mention-list {
  max-height: 264px;
  overflow-y: auto;

  /* 细滚动条：默认的 15px 滚动条会在这么窄的面板里显得很重 */
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(144, 147, 153, 0.3);
    border-radius: 2px;
  }
}

.mention-tip {
  padding: 24px 0;
  text-align: center;
  font-size: 13px;
  color: #a8abb2;

  /* 翻页时那一行，挨在已有列表后面，不需要空这么多 */
  &.is-more {
    padding: 10px 0;
    font-size: 12px;
  }
}

.mention-item {
  display: flex;
  align-items: center;
  padding: 8px;
  border-radius: 10px;
  /* 只过渡背景色。写 all 会把 hover 时的字色、图标一起带上，
     快速划过列表时每一行都在做一段动画，观感是"糊的" */
  transition: background-color 0.15s ease;

  &:hover {
    background-color: var(--hover-color);
  }

  /* 已经 @ 过的人点了也加不进去（onSelect 会 toast），所以不给可点的手型，
     右侧一个对勾表示"已在名单里"、昵称压成次要色。
     刻意不再把昵称染成 #3d7eff —— 那个蓝在正文里代表"可点击的 @提及"，
     用在这里会被读成"这一项是个链接" */
  &.is-selected {
    cursor: default;

    .mention-nickname {
      color: #a8abb2;
    }
  }
}

.mention-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-right: 10px;
  object-fit: cover;
  /* 头像取不到 / 是张透明图时，至少还是个中性的圆，而不是一块空白 */
  background-color: #f2f3f5;
}

/* min-width:0 是让 flex 子项能收缩、从而让省略号生效 */
.mention-nickname {
  flex: 1;
  min-width: 0;
  font-size: 15px;
  line-height: 22px;
  color: var(--article-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mention-check {
  flex-shrink: 0;
  margin-left: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #3d7eff;
}
</style>

<style lang="scss">
/*
  这一块必须是<b>非 scoped</b> 的：el-popover 的外壳（.el-popover）是 Element 自己创建、
  并且挂到 body 下面的，带不上本组件编译出来的 scope 属性，scoped 规则选不到它。
  作用范围靠 popper-class="mention-popover" 限定，不会影响别处的 popover。

  面板内容那部分仍然写在上面的 scoped 块里 —— 那些元素是本组件渲染的，
  身上带着 scope 属性，即便被 Element 移到 body 下也照样命中。
*/
.el-popover.mention-popover {
  /* 内边距收到 8px，让每一行的 hover 圆角块贴近卡片边缘（牛客的选人面板就是这个比例）。
     行自己带 8px 内边距，视觉上仍然有足够留白 */
  padding: 8px;
  border-radius: 12px;
  border: 1px solid var(--border-line);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.12);

  /* Element 默认给 popover 留 12px 的间隙，那是为箭头准备的。
     没有箭头、而且这是紧贴输入框的下拉列表，6px 更像"同一个控件的延伸" */
  &[x-placement^='bottom'] {
    margin-top: 6px;
  }

  &[x-placement^='top'] {
    margin-bottom: 6px;
  }
}
</style>
